import * as React from 'react'
import { ChildProps, compose } from 'react-apollo'
import { withRouter } from 'react-router-dom'
import { Prompt } from 'react-router'
import { ApolloClient, ApolloError } from 'apollo-client'
import { NormalizedCache } from 'apollo-cache-inmemory'
import * as Modal from 'react-modal'
import { History, Location } from 'history'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import * as R from 'ramda'

import { Store } from '../../reducers'
import { setTabulationLock } from '../../actions/tabulation'
import { Action } from '../../actions/types'
import ProjectFormHelper from './ProjectFormHelper'
import ConfirmCase from './ConfirmCase'
import { CountryType, withCountries } from '../Shared/CountryOptions'

import {
  TabWorkArea,
  ProjectDetailLeft,
  ProjectDetailRight,
  DetailRow,
  DetailRowBig,
  DescriptionRow,
  DetailTools,
  ButtonPrimary,
  ButtonNormal,
  modalStyle,
  ModalTools,
  ModalHead,
  ModalButtonNormal,
  ModalButtonPrimary,
  ModalMainText,
  Divider,
  ErrorMsg
} from './Styles'

import {
  ProjectType,
  ProjectInput,
  CaseInput,
  ProjectCaseTypePartial,
  UPDATE_PROJECT,
  UPDATE_PROJECT_CONTACT,
  SAVE_CASE,
  getProjects as getProjectsQuery,
  ProjectsResult
} from './projectQueries'

import {
  ProjectParametersResponse,
  withProjectParameters
} from './projectParameterQueries'
import { isLocationNorthAmerica } from 'src/location_changes/conversionHelper'

type ClientContext = {
  client: ApolloClient<NormalizedCache>
}

type ApolloQueryProps = {
  loading: boolean
  error?: ApolloError
}

type GetProjectsProps = {
  getProjects: ProjectsResult
} & ApolloQueryProps

type InputProps = {
  project?: ProjectType
  history: History
  setTabulationLock: (lock: boolean) => Action
  tabulationLocked: boolean
  histLocation: Location
  countries: CountryType[]
}

type CaseFormData = {
  id: string
  name: string
  description: string
  lastReport: string | null // URL of the last generated report, if available
  lastResults: string | null // ID of the last result set
  fireResistance: string
  beamMaterial: string
  beamConcreteGrade: string
  beamExtra: string
  execution: string
  finishing: string
  spaceType: string
  overRide: boolean
}

export type FormDataParams = {
  fireResistance: string
  beamMaterial: string
  beamConcreteGrade: string
  beamExtra: string
  execution: string
  finishing: string
  spaceType: string
}

type ProjectFormData = {
  id: string | null
  name: string
  description: string
  location: string
  designer: string
  company: string
  created: string
  modified: string
  cases: CaseFormData[]
} & FormDataParams

type State = {
  formData: ProjectFormData
  editParams: boolean
  editContact: boolean
  busy: boolean
  confirmModal: boolean
  error: string | null
}

// We use checkParams() for both Project and ProjectCase but the data sets are no longer the same.
// skipFireResistance allows us to skip relevant check (cases no longer carry fire resistance data)
// but it would probably be better to split this up.
export function checkParams(
  // tslint:disable-next-line
  formData,
  data: Partial<ProjectParametersResponse>,
  skipFireResistance: boolean = false
) {
  if (!data) {
    return false
  }
  const {
    beamConcreteGrades = [],
    beamMaterials = [],
    beamExtras = [],
    spaceTypes = [],
    fireResistances = [],
    executionTypes = [],
    finishingTypes = []
  } = data
  const {
    spaceType,
    fireResistance,
    beamMaterial,
    beamConcreteGrade,
    beamExtra,
    execution,
    finishing
  } = formData

  let fireResistanceOk = R.any(
    param => param.id === fireResistance,
    fireResistances
  )

  if (skipFireResistance) {
    fireResistanceOk = true
  }

  return (
    fireResistanceOk &&
    R.any(param => param.id === beamMaterial, beamMaterials) &&
    R.any(param => param.id === beamConcreteGrade, beamConcreteGrades) &&
    R.any(param => param.id === beamExtra, beamExtras) &&
    (R.any(param => param.id === execution, executionTypes) || isLocationNorthAmerica()) &&
    (R.any(param => param.id === finishing, finishingTypes) || isLocationNorthAmerica()) &&
    R.any(param => param.id === spaceType, spaceTypes)
  )
}

const formDataDef: ProjectFormData = {
  id: null,
  name: '',
  description: '',
  location: '',
  fireResistance: '',
  beamMaterial: '',
  beamConcreteGrade: '',
  beamExtra: '',
  execution: '',
  finishing: '',
  designer: '',
  company: '',
  spaceType: '',
  created: '',
  modified: '',
  cases: []
}

const fieldNames = {
  name: 'Project name',
  location: 'Location',
  created: 'Created',
  modified: 'Modified',
  fireResistance: 'Fire resistance',
  materials: 'Materials',
  beamMaterial: 'Materials',
  beamConcreteGrade: '',
  beamExtra: '',
  execution: 'Execution class',
  finishing: 'Finishing',
  description: 'Description',
  designer: 'Designer',
  company: 'Company',
  spaceType: 'Space type'
}

if (process.env.NODE_ENV !== 'test') {
  Modal.setAppElement('#root')
}

const prompt = location => ({ pathname }) =>
  'You have unchanged edits that will not be saved if you leave the page'

class ProjectDetails extends React.Component<
  ChildProps<InputProps, ProjectParametersResponse> & GetProjectsProps,
  State
> {
  static contextTypes = {
    client: () => null
  }

  context: ClientContext
  ParamsForm: ProjectFormHelper
  ContactForm: ProjectFormHelper

  constructor(props: InputProps & GetProjectsProps) {
    super(props)
    this.state = {
      formData: this._getFormData(props),
      editParams: false,
      editContact: false,
      busy: false,
      confirmModal: false,
      error: null
    }
    this.ParamsForm = new ProjectFormHelper({
      setState: (changed: object) => {
        const newFormData: object = {
          formData: {
            ...this.state.formData,
            ...changed
          }
        }
        this.setState(newFormData)
      },
      editMode: false,
      fieldNames
    })
    this.ContactForm = new ProjectFormHelper({
      setState: (changed: object) => {
        const newFormData: object = {
          formData: {
            ...this.state.formData,
            ...changed
          }
        }
        this.setState(newFormData)
      },
      editMode: false,
      fieldNames
    })
  }

  _getFormCaseData(
    projectCase: ProjectCaseTypePartial,
    formData: ProjectFormData
  ) {
    const {
      id,
      name,
      description,
      parameters,
      spaceType,
      lastReport,
      lastResults
    } = projectCase
    const params = {
      fireResistance: '',
      beamMaterial: '',
      beamConcreteGrade: '',
      beamExtra: '',
      execution: '',
      finishing: '',
      ...JSON.parse(parameters)
    }
    // deafault overrides i.e. allready same values
    const overRide =
      spaceType === formData.spaceType &&
      Object.keys(params)
        .map(key => params[key] === formData[key])
        .reduce((prev, curr) => prev === curr, true)
    return {
      id,
      name,
      description,
      lastReport,
      lastResults,
      spaceType,
      ...params,
      overRide
    }
  }

  _getFormData(props: InputProps): ProjectFormData {
    const project = props.project
    if (project) {
      const {
        id,
        name,
        location,
        created,
        modified,
        description,
        parameters,
        designer,
        company,
        spaceType,
        cases
      } = project
      const params = {
        fireResistance: '',
        beamMaterial: '',
        beamConcreteGrade: '',
        beamExtra: '',
        execution: '',
        finishing: '',
        ...JSON.parse(parameters)
      }
      const formData: ProjectFormData = {
        id,
        name,
        location,
        created,
        modified,
        ...params,
        description,
        designer,
        company,
        spaceType,
        cases: []
      }
      const projectCases = cases.map(projectCase =>
        this._getFormCaseData(projectCase, formData)
      )
      return {
        ...formData,
        cases: projectCases
      }
    }
    return formDataDef
  }

  componentWillReceiveProps(nextProps: InputProps) {
    const { project } = nextProps
    const prevId = this.props.project ? this.props.project.id : null
    if (project && project.id !== prevId) {
      this.setState({ formData: this._getFormData(nextProps) })
    }
  }

  closeConfirmModal = e => {
    const self = this
    // Which cases where selecetd?
    const { formData } = this.state
    const overrideList = formData.cases.filter(
      projectCase => projectCase.overRide
    )

    this.setState({ confirmModal: false, busy: true }, () =>
      self.onSubmitConfirm(overrideList)
    )
    e.preventDefault()
  }

  rejectConfirmModal = e => {
    this.setState({ confirmModal: false })
    e.preventDefault()
  }

  onSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    if (this.state.editParams) {
      this.setState({ confirmModal: true })
    } else {
      this.onSaveContacts()
    }
    e.preventDefault()
  }

  //
  // Saving edited parameters after confirmation of case overrides
  //
  onSubmitConfirm = (overRideList: CaseFormData[]) => {
    const { formData } = this.state

    const {
      fireResistance,
      beamMaterial,
      beamConcreteGrade,
      beamExtra,
      execution,
      finishing,
      id,
      name,
      description,
      location,
      spaceType
    } = formData
    const parameters = {
      fireResistance,
      beamMaterial,
      beamConcreteGrade,
      beamExtra,
      execution,
      finishing
    }
    const project: ProjectInput = { name, description, location, spaceType }

    const client = this.context.client

    client
      .mutate<Response>({
        mutation: UPDATE_PROJECT,
        variables: {
          projectId: id,
          project: project,
          params: JSON.stringify(parameters)
        }
      })
      .then(({ data }) => {
        return Promise.all(
          overRideList.map((formCase: CaseFormData) => {
            const {
              id: caseId,
              name: caseName,
              description: caseDescription
            } = formCase
            const projectCase: CaseInput = {
              id: caseId,
              name: caseName,
              description: caseDescription
            }

            return client.mutate<Response>({
              mutation: SAVE_CASE,
              variables: {
                case: projectCase,
                spaceType: spaceType,
                params: JSON.stringify(parameters)
              }
            })
          })
        )
      })
      .then(() => {
        this.setState({ busy: false, error: null }, () => {
          this.toggleEditParams(false)
        })
      })
      .catch(err => {
        console.warn('Save failed', err)
        this.setState({ busy: false, error: 'Save failed' })
      })
  }

  //
  // Saving edited contact infos
  //
  onSaveContacts = () => {
    const { formData } = this.state

    const { id, designer, company } = formData

    const client = this.context.client

    client
      .mutate<Response>({
        mutation: UPDATE_PROJECT_CONTACT,
        variables: {
          projectId: id,
          designer: designer,
          company: company
        }
      })
      .then(({ data }) => {
        this.setState({ busy: false, error: null }, () => {
          this.toggleEditContact(false)
        })
      })
      .catch(err => {
        console.log('Save failed', err)
        this.setState({ busy: false, error: 'Save failed' })
      })
  }

  toggleEditParams(editMode: boolean, reset?: boolean) {
    this.ParamsForm.toggleEditMode(editMode)
    if (reset) {
      this.setState(
        {
          editParams: editMode,
          error: null,
          formData: this._getFormData(this.props)
        },
        () => this.props.setTabulationLock(editMode)
      )
    } else {
      this.setState({ editParams: editMode }, () =>
        this.props.setTabulationLock(editMode)
      )
    }
  }

  toggleEditContact(editMode: boolean, reset?: boolean) {
    this.ContactForm.toggleEditMode(editMode)
    if (reset) {
      this.setState(
        {
          editContact: editMode,
          error: null,
          formData: this._getFormData(this.props)
        },
        () => this.props.setTabulationLock(editMode)
      )
    } else {
      this.setState({ editContact: editMode }, () =>
        this.props.setTabulationLock(editMode)
      )
    }
  }

  getCaseOverRide = id => {
    const { formData } = this.state
    const found = R.findIndex(
      projectCase => projectCase.id === id,
      formData.cases
    )
    return found > -1 ? formData.cases[found].overRide : false
  }

  setCaseOverRide = (id, val) => {
    const { formData } = this.state
    const found = R.findIndex(
      projectCase => projectCase.id === id,
      formData.cases
    )
    if (found > -1) {
      formData.cases[found].overRide = val
      this.setState({ formData })
    }
  }

  render() {
    const {
      formData,
      editParams,
      editContact,
      busy,
      error,
      confirmModal
    } = this.state
    const {
      id,
      name,
      location,
      created,
      modified,
      description,
      fireResistance,
      designer,
      company,
      beamMaterial,
      beamConcreteGrade,
      beamExtra,
      execution,
      finishing,
      spaceType
    } = formData
    const {
      history,
      histLocation,
      data,
      getProjects,
      project,
      countries = []
    } = this.props
    const ParamsForm = this.ParamsForm
    const ContactForm = this.ContactForm

    if (
      !formData ||
      !data ||
      !data.spaceTypes ||
      !data.fireResistances ||
      !data.executionTypes ||
      !data.finishingTypes ||
      !data.beamConcreteGrades ||
      !data.beamMaterials ||
      !data.beamExtras
    ) {
      return null
    }

    const originalName = project ? project.name : ''
    const nameUnique = !R.contains(
      name,
      R.filter(
        projName => projName !== originalName,
        R.map(proj => proj.name, getProjects || [])
      )
    )

    const projectCases = formData.cases.map(projectCase => {
      const { id: caseId, name: caseName } = projectCase

      return (
        <ConfirmCase
          key={caseId}
          caseName={caseName}
          getUseDefaults={() => this.getCaseOverRide(caseId)}
          setUseDefaults={val => this.setCaseOverRide(caseId, val)}
        />
      )
    })

    const paramsOk =
      checkParams(formData, data) && name.length > 0 && nameUnique

    const materials = [
      (
        R.find(p => p.id === beamMaterial, data.beamMaterials) || {
          displayName: '-unknown-'
        }
      ).displayName,
      (
        R.find(p => p.id === beamConcreteGrade, data.beamConcreteGrades) || {
          displayName: '-unknown-'
        }
      ).displayName,
      (
        R.find(p => p.id === beamExtra, data.beamExtras) || {
          displayName: '-unknown-'
        }
      ).displayName
    ].join(', ')

    // Find location display name regardless is location country id or country name
    const locationName = (
      R.find(c => c.id === location, countries) || { displayName: location }
    ).displayName

    return (
      <TabWorkArea>
        {busy && <div className="spinner" />}
        {(error || !nameUnique) && (
          <ErrorMsg>{!nameUnique ? 'Name already in use!' : error}</ErrorMsg>
        )}
        <form onSubmit={this.onSubmit}>
          <ProjectDetailLeft>
            <DetailRowBig>
              {ParamsForm.renderEditField('name', name, '9em', true, true)}
              {ParamsForm.renderTextField('location', locationName, null)}
              {editParams && !editContact ? (
                <DetailTools>
                  <ButtonPrimary
                    type={'button'}
                    onClick={() => this.toggleEditParams(false, true)}
                  >
                    CANCEL
                  </ButtonPrimary>
                  <ButtonNormal
                    type={'submit'}
                    disabled={!paramsOk}
                    title={
                      paramsOk ? 'Save changes' : 'Invalid or missing fields'
                    }
                  >
                    SAVE
                  </ButtonNormal>
                </DetailTools>
              ) : (
                <DetailTools>
                  <ButtonPrimary
                    type={'button'}
                    disabled={editContact}
                    onClick={() => this.toggleEditParams(true)}
                  >
                    EDIT
                  </ButtonPrimary>
                  <ButtonNormal
                    type={'button'}
                    disabled={editContact}
                    onClick={() => history.push(`/project/${id}/workspace`)}
                  >
                    OPEN
                  </ButtonNormal>
                </DetailTools>
              )}
            </DetailRowBig>
            <DetailRow>
              {ParamsForm.renderSelectField(
                'spaceType',
                spaceType,
                data.spaceTypes,
                '10em',
                true
              )}
              {editParams &&
                ParamsForm.renderSelectField(
                  'beamMaterial',
                  beamMaterial,
                  data.beamMaterials,
                  '10em',
                  true
                )}
              {editParams &&
                ParamsForm.renderSelectField(
                  'beamConcreteGrade',
                  beamConcreteGrade,
                  data.beamConcreteGrades,
                  '10em',
                  true
                )}
              {editParams &&
                ParamsForm.renderSelectField(
                  'beamExtra',
                  beamExtra,
                  data.beamExtras,
                  '10em',
                  true
                )}
              {!editParams &&
                ParamsForm.renderTextField('materials', materials, '32em')}
            </DetailRow>
            <DetailRow>
              {ParamsForm.renderSelectField(
                'fireResistance',
                fireResistance,
                data.fireResistances,
                '10em',
                true
              )}
              {ParamsForm.renderSelectField(
                'execution',
                execution,
                data.executionTypes,
                '10em',
                true
              )}
              {ParamsForm.renderSelectField(
                'finishing',
                finishing,
                data.finishingTypes,
                '10em',
                true
              )}
            </DetailRow>
            <DetailRow>
              {ParamsForm.renderDateField('created', created, '10em')}
              {ParamsForm.renderDateField('modified', modified, '10em')}
            </DetailRow>
            <DescriptionRow>
              {ParamsForm.renderEditTextArea(
                'description',
                description,
                null,
                false
              )}
            </DescriptionRow>
          </ProjectDetailLeft>
          <ProjectDetailRight>
            <DetailRowBig>
              {!editParams && editContact ? (
                <DetailTools>
                  <ButtonPrimary
                    type={'button'}
                    onClick={() => this.toggleEditContact(false, true)}
                  >
                    CANCEL
                  </ButtonPrimary>
                  <ButtonNormal
                    type={'submit'}
                    disabled={!paramsOk}
                    title={
                      paramsOk ? 'Save changes' : 'Invalid or missing fields'
                    }
                  >
                    SAVE
                  </ButtonNormal>
                </DetailTools>
              ) : (
                <DetailTools>
                  <ButtonPrimary
                    type={'button'}
                    disabled={editParams}
                    onClick={() => this.toggleEditContact(true)}
                  >
                    EDIT
                  </ButtonPrimary>
                </DetailTools>
              )}
            </DetailRowBig>
            <DetailRow>
              {ContactForm.renderEditField('designer', designer, '14em')}
            </DetailRow>
            <DetailRow>
              {ContactForm.renderEditField('company', company, '14em')}
            </DetailRow>
          </ProjectDetailRight>
        </form>
        {confirmModal && (
          <Modal
            isOpen={confirmModal}
            onRequestClose={this.rejectConfirmModal}
            style={modalStyle}
          >
            <ModalHead>Warning</ModalHead>
            <form style={{ padding: '0 1em' }}>
              <div style={{ margin: '1em 0' }}>
                <ModalMainText>
                  This will affect project cases settings.
                </ModalMainText>
                <ModalMainText>
                  Select cases with default settings:
                </ModalMainText>
              </div>
              <div style={{ padding: '0 1em' }}>
                {projectCases}
                <Divider />
              </div>
              <ModalTools>
                <ModalButtonNormal
                  type={'submit'}
                  onClick={this.closeConfirmModal}
                >
                  SAVE
                </ModalButtonNormal>
                <ModalButtonPrimary
                  type={'button'}
                  onClick={this.rejectConfirmModal}
                >
                  CANCEL
                </ModalButtonPrimary>
              </ModalTools>
            </form>
          </Modal>
        )}
        <Prompt
          message={prompt(histLocation)}
          when={editParams || editContact}
        />
      </TabWorkArea>
    )
  }
}

const mapStateToProps = (state: Store.Complete) => ({
  tabulationLocked: state.tabulation.locked
})

const mapDispatchToProps = (dispatch: Dispatch<Store.Complete>) => ({
  setTabulationLock: lock => dispatch(setTabulationLock(lock))
})

export default compose(
  withRouter,
  withCountries,
  connect(mapStateToProps, mapDispatchToProps),
  getProjectsQuery<GetProjectsProps>(),
  withProjectParameters
)(ProjectDetails)



// WEBPACK FOOTER //
// ./src/Components/Projects/ProjectDetails.tsx